import { useMemo, useRef } from 'react';
import { Button, Spinner } from '@monksflow/monks-ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { resolveWidgetThumbnailComponent } from '@/shared/ui/WidgetThumbnail';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks/useDebounce/useDebounce';
import { workspaceArtifactsInfiniteQuery } from '../../api/artifacts.api';
import {
  getPickerArtifactDetailKey,
  toPickerArtifactTuples,
  usePickerArtifactDetails,
} from '../../api/usePickerArtifactDetails';
import {
  resolveWorkspaceArtifactSort,
  WORKSPACE_ARTIFACT_SORT_CHOICES,
} from '../../lib/workspace-artifact-sort';
import type {
  ExistingArtifactsPickerProps,
  WorkspaceArtifactRow,
} from './ExistingArtifactsPicker.types';
import { PickerTable } from './PickerTable';
import { PickerToolbar } from './PickerToolbar';
import { usePickerSelection } from './usePickerSelection';
import { useTranslation } from 'react-i18next';

/** Maps a picker row to its batch-detail cache key — the `(contextId,key,version)` tuple. */
function getRowDetailKey(row: WorkspaceArtifactRow): string {
  return getPickerArtifactDetailKey({
    contextId: row.contextId,
    key: row.key,
    version: row.version ?? 0,
  });
}

export function ExistingArtifactsPicker(props: ExistingArtifactsPickerProps) {
  const { t } = useTranslation('common');

  const debouncedSearch = useDebounce(props.searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  const sortParams = resolveWorkspaceArtifactSort(props.sort);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery(
    workspaceArtifactsInfiniteQuery(props.workspaceId, {
      kind: props.kindFilter ?? undefined,
      search: trimmedSearch || undefined,
      sortBy: sortParams?.sortBy,
      sortOrder: sortParams?.sortOrder,
      isUserInput: props.isUserInput,
    }),
  );

  const allArtifacts = useMemo<WorkspaceArtifactRow[]>(
    () => (data?.pages.flatMap((p) => p.artifacts) ?? []) as WorkspaceArtifactRow[],
    [data],
  );

  // Resolve render-data for ALL loaded rows, not just the latest page: the
  // returned map is built from this query's data alone, so a last-page-only
  // tuple list would drop earlier rows back to the palette icon after "Load
  // more". `usePickerArtifactDetails` chunks at the api batch cap, so a growing
  // list stays within per-request bounds.
  const detailTuples = useMemo(
    () =>
      toPickerArtifactTuples(allArtifacts, (key) => resolveWidgetThumbnailComponent(key) != null),
    [allArtifacts],
  );

  const dataByRowKey = usePickerArtifactDetails(props.workspaceId, detailTuples);

  // Scroll element the row virtualizer measures against (PickerTable windows
  // the rows so it only mounts widgets near the viewport).
  const scrollRef = useRef<HTMLDivElement>(null);

  const selection = usePickerSelection({
    rows: allArtifacts,
    selectedIds: props.selectedIds,
    attachedIds: props.attachedIds,
    onSelectedIdsChange: props.onSelectedIdsChange,
  });

  const hasFilter = props.kindFilter !== null || trimmedSearch.length > 0;

  // Key-based reset for the shift-click anchor: when the row list changes
  // shape (sort / filter / search), the PickerTable subtree remounts and
  // the anchor naturally clears with it — replaces the prior
  // `useEffect(() => { lastSelectedIndexRef.current = null }, [...])` to
  // satisfy `no-use-effect`.
  const tableResetKey = `${trimmedSearch}-${props.kindFilter ?? ''}-${props.sort ?? ''}`;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <PickerToolbar
        searchInput={props.searchInput}
        onSearchInputChange={props.onSearchInputChange}
        searchAriaLabel={t('artifact.existingArtifactsPicker.searchArtifacts')}
        kindFilter={props.kindFilter}
        onKindFilterChange={props.onKindFilterChange}
        sort={{
          options: WORKSPACE_ARTIFACT_SORT_CHOICES,
          value: props.sort,
          onChange: props.onSortChange,
          resetOptionLabel: t('artifact.existingArtifactsPicker.newestFirst'),
        }}
      />

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border **:data-[slot=datatable-container]:overflow-visible"
        data-testid="existing-artifacts-picker-scroll-region"
      >
        <PickerTable
          key={tableResetKey}
          rows={allArtifacts}
          isLoading={isLoading}
          hasFilter={hasFilter}
          selectedIds={props.selectedIds}
          attachedIds={props.attachedIds}
          scrollRef={scrollRef}
          dataByRowKey={dataByRowKey}
          getRowDetailKey={getRowDetailKey}
          selectableIdCount={selection.selectableIdCount}
          allSelectableSelected={selection.allSelectableSelected}
          someSelectableSelected={selection.someSelectableSelected}
          onToggleSelectAll={selection.onToggleSelectAll}
          onRowActivate={selection.onRowActivate}
        />
      </div>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => {
              void fetchNextPage();
            }}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage && <Spinner />}
            {t('artifact.existingArtifactsPicker.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
